import * as dateFns from 'date-fns'
import React from 'react'

import { RedGreenText } from '../../../components/RedGreenText'
import { formatNumber, shortFormatNumber } from '../../../util'
import { EarningReportReaction, EarningsEstimateType, PriceReactionWhen } from '../types'
import { isAftermarket } from '../utils'
import { ChartYearData } from './EarningsChart'

const ESTIMATE_TYPE_LABELS: { [K in EarningsEstimateType]: string } = {
  [EarningsEstimateType.eps]: 'EPS',
  [EarningsEstimateType.epsGaap]: 'GAAP EPS',
  [EarningsEstimateType.sales]: 'Revenue',
}

type DailyChangeProps = {
  dateClassName?: string
  reportReaction: EarningReportReaction
  when: PriceReactionWhen
}

function DailyChange({ dateClassName, reportReaction, when }: DailyChangeProps) {
  const reaction = reportReaction.reactions[when]
  if (!reaction || !reaction.price || !reaction.prevPrice) {
    return null
  }
  const date = dateFns.parseISO(reaction.date)
  const diff = reaction.price - reaction.prevPrice
  return (
    <>
      <div className={dateClassName}>{dateFns.format(date, 'MMM dd')}</div>
      <div className="text-right">
        <RedGreenText value={diff} rightContent={null}>
          {formatNumber(diff)} ({formatNumber(Math.abs(reaction.priceDiff))}%)
        </RedGreenText>
      </div>
    </>
  )
}

type PerformanceTooltipProps = {
  data: ChartYearData | undefined
  estimateType: EarningsEstimateType
  isActive: boolean
}

export function PerformanceTooltip({ data, estimateType, isActive }: PerformanceTooltipProps) {
  if (!estimateType || !isActive || !data || !data.reportReaction) return null

  const reportReaction = data.reportReaction
  const earningsDate = dateFns.parseISO(reportReaction.reportDate)
  const fiscalEndDate = reportReaction.fiscalEndDate ? dateFns.parseISO(reportReaction.fiscalEndDate) : null

  let estimate
  let reported
  switch (estimateType) {
    case EarningsEstimateType.eps:
      estimate = data.epsEstimate
      reported = data.epsActual
      break
    case EarningsEstimateType.epsGaap:
      estimate = data.epsReportedEstimate
      reported = data.epsReportedActual
      break
    case EarningsEstimateType.sales:
      estimate = data.salesEstimate
      reported = data.salesActual
      break
    default:
      return null
  }

  return (
    <div className="relative -top-8 flex items-start rounded-md border border-secondary bg-primary font-sans text-2xs leading-4 text-muted-2">
      <div className="grid w-57.5 grid-cols-[96px_auto] space-y-1.5 p-2.5">
        <div className="col-span-2 text-sm text-muted font-medium">{ESTIMATE_TYPE_LABELS[estimateType]}</div>
        <div>Report Date</div>
        <div className="text-default">
          {dateFns.format(earningsDate, 'MMM dd, yyy')} {isAftermarket(earningsDate) ? 'AMC' : 'BMO'}
        </div>
        <div>Fiscal Period</div>
        <div className="text-default">{`${reportReaction.fiscalPeriod.substring(4, 6)} ${reportReaction.fiscalPeriod.substring(0, 4)}`}</div>
        <div>Period Ending</div>
        <div className="text-default">{fiscalEndDate && dateFns.format(fiscalEndDate, 'MMM yyyy')}</div>
        <div className="col-span-2">&nbsp;</div>
        <div>Estimate</div>
        <div className="text-default">{shortFormatNumber(estimate)}</div>
        <div>Reported</div>
        <div className="text-default">{shortFormatNumber(reported)}</div>
        <div>Surprise</div>
        <div className="font-medium">
          {data.surprise !== null && estimate && (
            <RedGreenText value={data.surprise} rightContent={null}>
              {shortFormatNumber(data.surprise)} ({formatNumber(Math.abs((data.surprise / estimate) * 100))}%)
            </RedGreenText>
          )}
        </div>
      </div>
      <div className="h-full border-l border-dashed border-secondary">
        <div className="grid w-37.5 space-y-1.5 text-nowrap p-2.5">
          <div className="col-span-2 text-2xs text-muted font-medium">Daily Change</div>
          <DailyChange reportReaction={reportReaction} when={PriceReactionWhen.MINUS_DAYS_3} />
          <DailyChange reportReaction={reportReaction} when={PriceReactionWhen.MINUS_DAYS_2} />
          <DailyChange reportReaction={reportReaction} when={PriceReactionWhen.MINUS_DAYS_1} />
          <DailyChange dateClassName="text-default" reportReaction={reportReaction} when={PriceReactionWhen.CLOSE} />
          <DailyChange reportReaction={reportReaction} when={PriceReactionWhen.PLUS_DAYS_1} />
          <DailyChange reportReaction={reportReaction} when={PriceReactionWhen.PLUS_DAYS_2} />
          <DailyChange reportReaction={reportReaction} when={PriceReactionWhen.PLUS_DAYS_3} />
        </div>
      </div>
    </div>
  )
}
